<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError }>()

const { t } = useI18n()
const localePath = useLocalePath()

// Vue Router records the previous route's path in history state; if present we
// can offer a real "back" instead of always dumping the user on the homepage.
const previousPath = ref<string | null>(null)
onMounted(() => {
  previousPath.value = (window.history.state?.back as string | null) ?? null

  // A 401 that got this far means a token that was still inside its `exp` but had
  // been revoked backend-side (`token_version` bump) — `gate.global.ts` renews an
  // *expired* token before rendering, but it cannot see a revoked one, so the first
  // sign is a 401 from the page's own fetch.
  //
  // Clearing the cookie here, in the browser, is what makes that recoverable. The
  // mid-render `logout()` also clears it, but a fatal error short-circuits the
  // `app:rendered` / `app:redirected` hooks Nuxt uses to flush server-side cookie
  // writes, so `passport_v1` survived and every reload reproduced the same 500.
  if (props.error.statusCode === 401) {
    useCookie(PASSPORT_COOKIE_KEY, passportCookieOptions()).value = null
    const target = localePath({ name: LOGIN_ROUTE_NAME })
    clearError({ redirect: `${target}?expired=1` })
  }
})

const handleError = () => clearError({ redirect: previousPath.value || '/' })

const statusMessage = computed(() => {
  if (props.error.statusCode == 400 || props.error.statusCode == 404) {
    return t('errors.pageNotFound')
  }

  return t('errors.server')
})


const isDevelopment = import.meta.dev

useSeoMeta({
  title: `${props.error.statusCode} - ${t('errors.title')}`,
  description: t('errors.server')
})
</script>

<template>
  <div class="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
    <p class="text-lg font-semibold uppercase tracking-wide text-brand-dark-blue">{{ $t('errors.title') }}</p>
    <h1 class="text-6xl font-bold text-gray-800">{{ error.statusCode }}</h1>
    <p class="text-xl text-gray-600">{{ statusMessage }}</p>
    <p v-if="isDevelopment && error.message" class="max-w-md text-sm text-gray-400">
      {{ error.message }}
    </p>
    <button
      type="button"
      class="mt-4 rounded-lg bg-brand-dark-blue px-6 py-2 text-white transition-colors  cursor-pointer"
      @click="handleError"
    >
      {{ previousPath ? $t('errors.goBack') : $t('errors.backToHome') }}
    </button>
  </div>
</template>
